// =====================================================================
// 假行 / JiaFlight — Pinia auth store
// 管理 token、用户信息
// =====================================================================

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi, type MeResponse } from '@/api/endpoints';
import { clearToken, getToken, setToken } from '@/api/client';
import type { UserProfile } from '@jiaflight/shared';

export const useAuthStore = defineStore('auth', () => {
  // ========== state ==========
  const user = ref<{ id: string; email: string } | null>(null);
  const profile = ref<UserProfile | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const adminFlag = ref(false);

  // ========== getters ==========
  // 注意：user.value 必须在前，确保被响应式系统追踪
  // getToken() 读 localStorage 不是响应式的，放前面会短路导致 user.value 不被收集为依赖
  const isAuthenticated = computed(() => !!user.value && !!getToken());
  const isAdmin = computed(() => adminFlag.value);

  // ========== actions ==========

  /** 登录 */
  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const result = await authApi.login(email, password);
      setToken(result.accessToken, result.refreshToken);
      user.value = result.user;
      // 登录后立即获取 profile
      await fetchMe();
    } catch (err) {
      error.value = err instanceof Error ? err.message : '登录失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /** 注册 */
  async function register(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await authApi.register(email, password);
      // 注册成功后自动登录
      await login(email, password);
    } catch (err) {
      error.value = err instanceof Error ? err.message : '注册失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /** 获取当前用户信息 */
  async function fetchMe(): Promise<void> {
    if (!getToken()) return;
    try {
      const me: MeResponse = await authApi.me();
      user.value = { id: me.id, email: me.email };
      profile.value = me.profile;
      adminFlag.value = me.isAdmin ?? false;
    } catch (err) {
      console.warn('[auth] fetchMe 失败:', err);
      // token 无效，清除
      logout();
    }
  }

  /** 更新用户偏好设置 */
  async function updateProfile(data: Partial<UserProfile>): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const updated = await authApi.updateProfile(data);
      profile.value = updated;
    } catch (err) {
      error.value = err instanceof Error ? err.message : '更新设置失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /** 退出登录 */
  function logout(): void {
    clearToken();
    user.value = null;
    profile.value = null;
    adminFlag.value = false;
  }

  return {
    user,
    profile,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    login,
    register,
    fetchMe,
    updateProfile,
    logout,
  };
});
